import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Platform, View } from 'react-native';
import ReactNativeModal from 'react-native-modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MB_Blur, MB_BlurProps } from '@/components/general/blur';
import { mbPlatformStyle, mbApplyTransparency } from '@/utils/general/style-utils';
import { MB_ModalAnimationProps } from '@/components/general/modal';

interface ModalForPopUpProps {
  /**
   * Whether the modal is visible or not.
   */
  isVisible?: boolean;

  /**
   * Called when the modal finishes getting dismissed.
   */
  onHide: () => void;

  /**
   * Modal's content.
   */
  children: React.JSX.Element;

  /**
   * Props to pass to the `MB_Blur` component.
   */
  blurProps?: MB_BlurProps;
  animationProps?: MB_ModalAnimationProps;
}

const ModalForPopUp = ({ isVisible, onHide, children, blurProps, animationProps }: ModalForPopUpProps) => {
  const reactNativeModalRef = useRef<ReactNativeModal>(null);
  const [dismissInProgress, setDismissInProgress] = useState(false);

  const backgroundColor = mbApplyTransparency('#222222', 0.7);

  const [idForForceUpdate, setIdForForceUpdate] = useState(1);

  // TODO: Note: With certain animations like zoom in, the blur stops working if we try to render it right away. We end up needing to
  // fore re-render it a bit late after the view is not super small in order to show the blur.
  useEffect(() => {
    setTimeout(() => setIdForForceUpdate(old => old + 1), 50);
    setTimeout(() => setIdForForceUpdate(old => old + 1), 100);
  }, []);

  return (
    <ReactNativeModal
      ref={reactNativeModalRef}
      animationInTiming={300}
      avoidKeyboard={false}
      coverScreen={true}
      hasBackdrop={false}
      backdropColor={'black'}
      backdropOpacity={0.7}
      isVisible={dismissInProgress ? false : isVisible}
      onModalHide={() => {
        setDismissInProgress(false);
        onHide();
      }}
      onBackButtonPress={() => {
        setDismissInProgress(true);
        reactNativeModalRef.current?.close();
      }}
      // iOS modals were flickering when using native driver
      useNativeDriver={Platform.OS === 'web' || Platform.OS === 'ios' ? false : true}
      useNativeDriverForBackdrop={Platform.OS === 'web' ? false : true}
      hideModalContentWhileAnimating={false}
      propagateSwipe={true}
      style={styles.modalStyle}
      pointerEvents={dismissInProgress || !isVisible ? 'none' : undefined}
      {...animationProps}
    >
      <SafeAreaView style={{ flex: 1 }}>
        <View style={styles.childrenWrapper}>
          <View style={[styles.blurWrapper, { backgroundColor }]}>
            <MB_Blur key={idForForceUpdate} style={{ width: '100%', height: '100%' }} {...blurProps} />
          </View>
          {children}
        </View>
      </SafeAreaView>
    </ReactNativeModal>
  );
};

export { ModalForPopUp };

const styles = StyleSheet.create({
  modalStyle: {
    margin: 0,
    flex: 1,
  },
  childrenWrapper: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  blurWrapper: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    ...mbPlatformStyle({
      web: {
        cursor: 'auto',
        overflow: 'hidden', // for safari, otherwise modals that are scrollable will have the blur applied to the top of the modal for some reason.
      },
    }),
  },
});
